import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';

import {
  ActionIcon,
  Box,
  Button,
  Group,
  Switch,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import {
  IconAlertCircle,
  IconFileExcel,
  IconInfoSquareRounded,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { dateToDateString } from '@utils/date';

import {
  Customer,
  GetCustomersRequestParams,
  GetCustomersSortBy,
} from '@interfaces/customer.interface';
import { User } from '@interfaces/users.interface';

import {
  useCountCustomersQuery,
  useGetCustomersQuery,
} from '@api/customer.api';
import {
  useActivateUserMutation,
  useDeactivateUserMutation,
  useDeleteUserMutation,
} from '@api/users.api';

import useAuth from '@hooks/useAuth';
import useStateWithDebounce from '@hooks/useStateWithDebouce';

import ApproveCustomerModal from '@components/ApproveCustomerModal';
import GetCustomersXlsxFilters from '@components/GetCustomersXlsxFilters';
import { LayoutContextType } from '@components/layout/Layout';
import MarketCategoriesSelector from '@components/MarketCategoriesSelector';

export default function CustomersList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { user } = useAuth();

  // ==========================================================================
  // State
  // ==========================================================================
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<GetCustomersRequestParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 50),
    sortBy: 'id',
    sortOrder: 'desc',
  });

  const [firstNameSearch, setFirstNameSearch, firstNameSearchDebounced] =
    useStateWithDebounce('');
  const [lastNameSearch, setLastNameSearch, lastNameSearchDebounced] =
    useStateWithDebounce('');
  const [emailSearch, setEmailSearch, emailSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Customer>>({
    columnAccessor: 'id',
    direction: 'desc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as GetCustomersSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: customerCount = { count: 0 }, isLoading: isLoadingCount } =
    useCountCustomersQuery({ firstNameSearch, lastNameSearch, emailSearch });

  const { data = [], isLoading } = useGetCustomersQuery({
    ...filters,
    firstNameSearch: firstNameSearchDebounced,
    lastNameSearch: lastNameSearchDebounced,
    emailSearch: emailSearchDebounced,
  });

  const [deleteUser] = useDeleteUserMutation();

  const [activateUser, { isLoading: isLoadingActivateUser }] =
    useActivateUserMutation();
  const [deactivateUser, { isLoading: isLoadingDeactivateUser }] =
    useDeactivateUserMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleApproveCustomer = (user: User) => {
    openModal({
      title: 'Approva cliente',
      children: <ApproveCustomerModal user={user} />,
    });
  };

  const handleDeleteUser = (user: User) => {
    openConfirmModal({
      title: 'Elimina utente',
      children: `Stai per eliminare l'utente ${user.firstName} ${user.lastName}. Confermare?`,
      labels: {
        cancel: 'Annulla',
        confirm: 'Elimina utente',
      },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteUser(user.id),
    });
  };

  const handleDownloadXlsx = () => {
    openModal({
      title: 'Esporta clienti',
      children: <GetCustomersXlsxFilters />,
    });
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: 'Clienti',
      rightSection: (
        <Button onClick={handleDownloadXlsx} leftSection={<IconFileExcel />}>
          Scarica excel
        </Button>
      ),
    });
  }, [setLayoutProps]);

  return (
    <DataTable
      fetching={isLoading || isLoadingCount}
      idAccessor="user.id"
      columns={[
        {
          accessor: 'user.active',
          title: 'Attivo',
          width: '0%',
          render: (record) => (
            <Group wrap="nowrap" gap="xs">
              {!record.user.adminApprovedAt && (
                <Tooltip label="Utente in attesa di approvazione">
                  <IconAlertCircle />
                </Tooltip>
              )}
              {record.user.activationTokenCreatedAt === null &&
              !record.user.adminApprovedAt ? (
                <Button
                  onClick={() => handleApproveCustomer(record.user)}
                  size="xs"
                >
                  Approva
                </Button>
              ) : (
                <Tooltip
                  label={
                    record.user.id === user?.id
                      ? 'Non puoi disabilitare il tuo account'
                      : record.user.activationTokenCreatedAt
                        ? "L'utente non ha ancora attivato il suo account"
                        : record.user.active
                          ? 'Disabilita'
                          : 'Abilita'
                  }
                >
                  <div>
                    <Switch
                      disabled={
                        record.user.id === user?.id ||
                        record.user.activationTokenCreatedAt !== null ||
                        isLoadingActivateUser ||
                        isLoadingDeactivateUser
                      }
                      checked={record.user.active}
                      onClick={
                        record.user.active
                          ? () => deactivateUser(record.user.id)
                          : () => activateUser(record.user.id)
                      }
                    />
                  </div>
                </Tooltip>
              )}
            </Group>
          ),
        },
        {
          accessor: 'firstName',
          title: 'Nome',
          sortable: true,
          render: (record) => record.user.firstName,
          filter: (
            <TextInput
              placeholder="Ricerca nome"
              leftSection={<IconSearch size={16} />}
              rightSection={
                firstNameSearch.trim() !== '' ? (
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    c="dimmed"
                    onClick={() => setFirstNameSearch('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : undefined
              }
              value={firstNameSearch}
              onChange={(e) => setFirstNameSearch(e.currentTarget.value)}
            />
          ),
        },
        {
          accessor: 'lastName',
          title: 'Cognome',
          sortable: true,
          render: (record) => record.user.lastName,
          filter: (
            <TextInput
              placeholder="Ricerca cognome"
              leftSection={<IconSearch size={16} />}
              rightSection={
                lastNameSearch.trim() !== '' ? (
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    c="dimmed"
                    onClick={() => setLastNameSearch('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : undefined
              }
              value={lastNameSearch}
              onChange={(e) => setLastNameSearch(e.currentTarget.value)}
            />
          ),
        },
        {
          accessor: 'email',
          title: 'Email',
          sortable: true,
          render: (record) => record.user.email,
          filter: (
            <TextInput
              placeholder="Ricerca email"
              leftSection={<IconSearch size={16} />}
              rightSection={
                emailSearch.trim() !== '' ? (
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    c="dimmed"
                    onClick={() => setEmailSearch('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : undefined
              }
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.currentTarget.value)}
            />
          ),
        },
        {
          accessor: 'allowedCategories',
          title: 'Mercati',
          render: (record) => record.allowedCategories.join(', '),
          filter: (
            <Box miw="15rem">
              <MarketCategoriesSelector
                label={undefined}
                value={filters.categories}
                onChange={(categories) =>
                  setFilters((filters) => ({ ...filters, categories }))
                }
              />
            </Box>
          ),
        },
        {
          accessor: 'createdAt',
          title: 'Data registrazione',
          sortable: true,
          render: (record) => dateToDateString(new Date(record.createdAt)),
        },
        {
          accessor: 'actions',
          title: '',
          width: '0%',
          textAlign: 'right',
          render: (record) => (
            <Group wrap="nowrap" gap="xs">
              <Tooltip label="Dettagli">
                <ActionIcon
                  variant="subtle"
                  component={Link}
                  to={`/utenti/${record.user.id}`}
                >
                  <IconInfoSquareRounded />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Elimina">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  disabled={record.user.id === user!.id}
                  onClick={() => handleDeleteUser(record.user)}
                >
                  <IconTrash />
                </ActionIcon>
              </Tooltip>
            </Group>
          ),
        },
      ]}
      records={data}
      page={filters.page!}
      onPageChange={(page) => setFilters((filters) => ({ ...filters, page }))}
      recordsPerPage={filters.pageLength!}
      totalRecords={customerCount.count}
      sortStatus={sortStatus}
      onSortStatusChange={setSortStatus}
      noRecordsText="Nessun dato"
      minHeight={data.length === 0 ? 160 : undefined}
      striped
      highlightOnHover
      withTableBorder
    />
  );
}
